import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { store } from './store';
import { theme } from './utils/theme';
import PrivateRoute from './components/PrivateRoute';
import Layout from './components/Layout';
import SessionErrorBoundary from './components/SessionErrorBoundary';
import RoleBasedRedirect from './components/RoleBasedRedirect';
import RoleProtectedRoute from './components/RoleProtectedRoute';

// Páginas
import LoginPage from './pages/LoginPage';
import ProductsPage from './pages/ProductsPage';
import InventoryPage from './pages/InventoryPage';
import BulkAdjustmentPage from './pages/BulkAdjustmentPage';
import SalesPage from './pages/SalesPage';
import LocationsPage from './pages/LocationsPage';
import ReportsPage from './pages/ReportsPage';
import CashDrawerPage from './pages/CashDrawerPage';
import BarcodePage from './pages/BarcodePage';
import MigrationPage from './pages/MigrationPage';
import SettingsPage from './pages/SettingsPage';
import UsersPage from './pages/UsersPage';
import CommissionsPage from './pages/CommissionsPage';
import AuditLogsPage from './pages/AuditLogsPage';
import SalesHistoryPage from './pages/SalesHistoryPage';
// import DiscountConfigPage from './pages/DiscountConfigPage'; // Disabled for security
import TransfersPage from './pages/TransfersPage';
import RastreoPage from './pages/RastreoPage';

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
          <CssBaseline />
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="colored"
          />
          <Router>
            <SessionErrorBoundary>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route
                  path="/"
                  element={
                    <PrivateRoute>
                      <Layout />
                    </PrivateRoute>
                  }
                >
                <Route index element={<RoleBasedRedirect />} />
                <Route path="products" element={<RoleProtectedRoute cashierAllowed><ProductsPage /></RoleProtectedRoute>} />
                <Route path="inventory" element={<RoleProtectedRoute cashierAllowed><InventoryPage /></RoleProtectedRoute>} />
                <Route path="inventory/bulk-adjustment" element={<RoleProtectedRoute cashierAllowed><BulkAdjustmentPage /></RoleProtectedRoute>} />
                <Route path="rastreo" element={<RoleProtectedRoute cashierAllowed><RastreoPage /></RoleProtectedRoute>} />
                <Route path="sales" element={<RoleProtectedRoute cashierAllowed><SalesPage /></RoleProtectedRoute>} />
                <Route path="transfers" element={<RoleProtectedRoute cashierAllowed><TransfersPage /></RoleProtectedRoute>} />
                <Route path="locations" element={<RoleProtectedRoute adminOnly><LocationsPage /></RoleProtectedRoute>} />
                <Route path="reports" element={<RoleProtectedRoute adminOnly><ReportsPage /></RoleProtectedRoute>} />
                <Route path="cash-drawer" element={<RoleProtectedRoute cashierAllowed><CashDrawerPage /></RoleProtectedRoute>} />
                <Route path="barcodes" element={<RoleProtectedRoute cashierAllowed><BarcodePage /></RoleProtectedRoute>} />
                <Route path="migration" element={<RoleProtectedRoute adminOnly><MigrationPage /></RoleProtectedRoute>} />
                <Route path="settings" element={<RoleProtectedRoute adminOnly><SettingsPage /></RoleProtectedRoute>} />
                <Route path="users" element={<RoleProtectedRoute adminOnly><UsersPage /></RoleProtectedRoute>} />
                <Route path="commissions" element={<RoleProtectedRoute adminOnly><CommissionsPage /></RoleProtectedRoute>} />
                <Route path="audit-logs" element={<RoleProtectedRoute adminOnly><AuditLogsPage /></RoleProtectedRoute>} />
                <Route path="sales-history" element={<RoleProtectedRoute adminOnly><SalesHistoryPage /></RoleProtectedRoute>} />
                {/* Discount configuration disabled for security */}
                {/* <Route path="discounts" element={<RoleProtectedRoute adminOnly><DiscountConfigPage /></RoleProtectedRoute>} /> */}
              </Route>
                {/* Catch-all: send unknown paths home instead of rendering a blank screen */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </SessionErrorBoundary>
          </Router>
        </LocalizationProvider>
      </ThemeProvider>
    </Provider>
  );
}

export default App;
